import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useExperiment } from '@/libraries/experiment';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed } from 'vue';

export function useOverProviderExp({
	offers,
	title,
	showFreeTrial,
}: {
	offers: MaybeRefOrGetter<TitleOfferFragment[]>;
	title: MaybeRefOrGetter<TitleDetail>;
	showFreeTrial: MaybeRefOrGetter<boolean>;
}) {
	const { catalogueItem } = usePromotion({ title, offers });
	const _offers = toValue(offers);
	const { activeVariantWithControlGroup: overProviderExpVariant } = useExperiment('OVER_PROVIDER_EXP_V2');

	// over provider "kicks in" when:
	// - there would be a FT
	// - and the provider of the FT is also in the offers
	const kickedInOverProviderTechnicalName = computed<string | undefined>(() => {
		const wouldShowFreeTrial =
			catalogueItem.value?.placement === 'ft' || (!catalogueItem.value?.placement && showFreeTrial);

		if (wouldShowFreeTrial) {
			// check if there are already offers of the same provider in the promotion
			if (_offers.find(item => item.package.technicalName === catalogueItem.value?.technicalName)) {
				return catalogueItem.value?.technicalName;
			}
		}

		return undefined;
	});

	const overProviderKicksIn = computed(() => !!kickedInOverProviderTechnicalName.value);

	function setOverProviderExpPromotedOfferTag(url: ClickoutUrl) {
		if (!overProviderExpVariant.value || !overProviderKicksIn.value) return url;

		switch (overProviderExpVariant.value) {
			case 'control':
				url.setOnOffer('tag', `justfr-opexp-control-21`);
				break;
			case 'variant_1':
				url.setOnOffer('tag', `justfr-opexp-v1-21`);
				break;
			case 'variant_2':
				url.setOnOffer('tag', `justfr-opexp-v2-21`);
				break;
		}

		return url.skipYieldOptimization().skipUCT2();
	}

	function setOverProviderExpRegularOfferTag(url: ClickoutUrl) {
		if (overProviderExpVariant.value !== 'control') return url;

		switch (overProviderExpVariant.value) {
			case 'control':
				url.setOnOffer('tag', `justfr-opexp-ro-control-21`);
				break;
		}

		return url.skipYieldOptimization().skipUCT2();
	}

	return {
		overProviderKicksIn,
		kickedInOverProviderTechnicalName,
		setOverProviderExpPromotedOfferTag,
		setOverProviderExpRegularOfferTag,
	};
}
